"use client";

import { useState } from "react";
import { Languages, Plus, X, Globe, AlertCircle, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const LANGUAGES = [
  { code: "en", name: "English" },
  { code: "es", name: "Spanish" },
  { code: "fr", name: "French" },
  { code: "de", name: "German" },
  { code: "it", name: "Italian" },
  { code: "pt", name: "Portuguese" },
  { code: "ja", name: "Japanese" },
  { code: "ko", name: "Korean" },
  { code: "zh", name: "Chinese" },
];

interface LanguageInfo {
  lang: string;
  name?: string;
  information: string;
  features?: Record<string, any>;
}

interface Language {
  code: string;
  name: string;
}

interface MultilangEditorProps {
  infos: LanguageInfo[];
  onChange: (infos: LanguageInfo[]) => void;
  languages?: Language[];
  required?: boolean;
  showFeatures?: boolean;
  showNames?: boolean;
  showDescriptions?: boolean;
  nameLabel?: string;
  descriptionLabel?: string;
}

export default function MultilangEditor({
  infos,
  onChange,
  languages = LANGUAGES,
  required = true,
  showFeatures = false,
  showNames = false,
  showDescriptions = true,
  nameLabel = "Name",
  descriptionLabel = "Description",
}: MultilangEditorProps) {
  const [activeTab, setActiveTab] = useState(infos[0]?.lang || "en");

  const availableLanguages = languages.filter(
    (lang) => !infos.some((info) => info.lang === lang.code),
  );

  const handleAddLanguage = (langCode: string) => {
    const language = languages.find((l) => l.code === langCode);
    if (!language) return;

    const newInfo: LanguageInfo = {
      lang: langCode,
      name: showNames ? "" : undefined,
      information: "",
      features: {},
    };

    onChange([...infos, newInfo]);
    setActiveTab(langCode);
  };

  const handleRemoveLanguage = (langCode: string) => {
    const newInfos = infos.filter((info) => info.lang !== langCode);
    onChange(newInfos);

    if (activeTab === langCode && newInfos.length > 0) {
      setActiveTab(newInfos[0].lang);
    }
  };

  const handleUpdateInfo = (langCode: string, information: string) => {
    const newInfos = infos.map((info) =>
      info.lang === langCode ? { ...info, information } : info,
    );
    onChange(newInfos);
  };

  const handleUpdateName = (langCode: string, name: string) => {
    const newInfos = infos.map((info) =>
      info.lang === langCode ? { ...info, name } : info,
    );
    onChange(newInfos);
  };

  const handleUpdateFeature = (
    langCode: string,
    feature: string,
    value: any,
  ) => {
    const newInfos = infos.map((info) =>
      info.lang === langCode
        ? { ...info, features: { ...info.features, [feature]: value } }
        : info,
    );
    onChange(newInfos);
  };

  const getLanguageName = (code: string) => {
    return languages.find((l) => l.code === code)?.name || code.toUpperCase();
  };

  const getCompletionStatus = (info: LanguageInfo) => {
    const hasName = !showNames || (info.name && info.name.trim().length > 0);
    const hasDescription =
      !showDescriptions ||
      (info.information && info.information.trim().length > 0);
    const hasFeatures =
      showFeatures && Object.keys(info.features || {}).length > 0;

    if (showFeatures) {
      return hasName && hasDescription && hasFeatures
        ? "complete"
        : hasName && hasDescription
          ? "partial"
          : "empty";
    }
    if (showNames && showDescriptions) {
      return hasName && hasDescription
        ? "complete"
        : hasName || hasDescription
          ? "partial"
          : "empty";
    }
    if (showNames) {
      return hasName ? "complete" : "empty";
    }
    if (showDescriptions) {
      return hasDescription ? "complete" : "empty";
    }
    return "complete"; // If neither names nor descriptions are shown, always complete
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Languages className="h-5 w-5 text-muted-foreground" />
          <span className="text-sm font-medium">
            Translations ({infos.length} / {languages.length})
          </span>
        </div>

        {availableLanguages.length > 0 && (
          <Select onValueChange={handleAddLanguage}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Add language">
                <div className="flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Add language
                </div>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {availableLanguages.map((lang) => (
                <SelectItem key={lang.code} value={lang.code}>
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    {lang.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Language Tabs */}
      {infos.length > 0 ? (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full justify-start flex-wrap h-auto p-1">
            {infos.map((info) => {
              const status = getCompletionStatus(info);
              return (
                <TabsTrigger
                  key={info.lang}
                  value={info.lang}
                  className="relative"
                >
                  <span className="flex items-center gap-2">
                    {getLanguageName(info.lang)}
                    {status === "complete" && (
                      <Check className="h-3 w-3 text-green-600" />
                    )}
                    {status === "partial" && (
                      <div className="h-2 w-2 bg-yellow-500 rounded-full" />
                    )}
                  </span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {infos.map((info) => (
            <TabsContent
              key={info.lang}
              value={info.lang}
              className="space-y-4"
            >
              <Card className="p-4">
                <div className="space-y-4">
                  {/* Language Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">
                        {getLanguageName(info.lang)}
                      </span>
                      {info.lang === "en" && required && (
                        <Badge variant="secondary" className="text-xs">
                          Required
                        </Badge>
                      )}
                    </div>
                    {info.lang !== "en" && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveLanguage(info.lang)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>

                  {/* Name (Optional) */}
                  {showNames && (
                    <div className="space-y-2">
                      <Label htmlFor={`name-${info.lang}`}>
                        {nameLabel}
                        {required && info.lang === "en" && (
                          <span className="text-red-500 ml-1">*</span>
                        )}
                      </Label>
                      <Input
                        id={`name-${info.lang}`}
                        value={info.name || ""}
                        onChange={(e) =>
                          handleUpdateName(info.lang, e.target.value)
                        }
                        placeholder={`Enter ${nameLabel.toLowerCase()} in this language...`}
                      />
                    </div>
                  )}

                  {/* Description */}
                  {showDescriptions && (
                    <div className="space-y-2">
                      <Label htmlFor={`desc-${info.lang}`}>
                        {descriptionLabel}
                        {required && info.lang === "en" && (
                          <span className="text-red-500 ml-1">*</span>
                        )}
                      </Label>
                      <Textarea
                        id={`desc-${info.lang}`}
                        value={info.information || ""}
                        onChange={(e) =>
                          handleUpdateInfo(info.lang, e.target.value)
                        }
                        placeholder={`Enter ${descriptionLabel.toLowerCase()} in this language...`}
                        rows={4}
                        className="resize-none"
                      />
                      <p className="text-xs text-muted-foreground">
                        {(info.information || "").length} characters
                      </p>
                    </div>
                  )}

                  {/* Features (Optional) */}
                  {showFeatures && (
                    <div className="space-y-2">
                      <Label>Features & Highlights</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {["highlight1", "highlight2", "highlight3"].map(
                          (feature) => (
                            <input
                              key={feature}
                              type="text"
                              value={info.features?.[feature] || ""}
                              onChange={(e) =>
                                handleUpdateFeature(
                                  info.lang,
                                  feature,
                                  e.target.value,
                                )
                              }
                              placeholder={`Feature ${feature.slice(-1)}`}
                              className="px-3 py-2 border border-input bg-background rounded-md text-sm"
                            />
                          ),
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            </TabsContent>
          ))}
        </Tabs>
      ) : (
        <Card className="p-8">
          <div className="text-center text-muted-foreground">
            <Globe className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="text-sm mb-4">No translations added yet</p>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleAddLanguage("en")}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add English (Required)
            </Button>
          </div>
        </Card>
      )}

      {/* Validation Message */}
      {required &&
        (() => {
          const englishInfo = infos.find((info) => info.lang === "en");
          const missingName =
            showNames && (!englishInfo || !englishInfo.name?.trim());
          const missingDescription =
            showDescriptions &&
            (!englishInfo || !englishInfo.information.trim());

          if (missingName || missingDescription) {
            const missingFields = [];
            if (missingName) missingFields.push(nameLabel.toLowerCase());
            if (missingDescription)
              missingFields.push(descriptionLabel.toLowerCase());

            return (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <div className="flex items-center gap-2 text-yellow-800">
                  <AlertCircle className="h-4 w-4" />
                  <span className="text-sm">
                    English {missingFields.join(" and ")}{" "}
                    {missingFields.length > 1 ? "are" : "is"} required
                  </span>
                </div>
              </div>
            );
          }
          return null;
        })()}
    </div>
  );
}
